import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Clock, Phone, Mail, Navigation, PartyPopper } from "lucide-react";
import { siteData } from "../data/mock";

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export const VisitSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="visit" className="relative py-24 md:py-32 bg-[#0d0b08] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-gold/60 text-xs uppercase tracking-[0.4em] font-light">Visit</span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mt-4">
            {siteData.visit.title}
          </h2>
          <p className="text-cream/50 text-base mt-4 font-light max-w-xl mx-auto">
            {siteData.visit.subtitle}
          </p>
        </motion.div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Address & Directions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-[#161412] border border-gold/10 rounded-sm p-8 hover:border-gold/25 transition-colors duration-500"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <MapPin className="text-gold" size={18} />
              </div>
              <h3 className="font-heading text-xl text-cream">Location</h3>
            </div>
            <p className="text-cream/60 font-light leading-relaxed mb-6">
              {siteData.visit.address.line1}<br />
              {siteData.visit.address.line2}
            </p>
            <a
              href={siteData.visit.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold/90 text-[#0d0b08] text-sm font-medium uppercase tracking-[0.15em] rounded-sm hover:bg-gold transition-colors duration-300"
            >
              <Navigation size={14} />
              Get Directions
            </a>
          </motion.div>

          {/* Hours */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#161412] border border-gold/10 rounded-sm p-8 hover:border-gold/25 transition-colors duration-500"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <Clock className="text-gold" size={18} />
              </div>
              <h3 className="font-heading text-xl text-cream">Hours</h3>
            </div>
            <div className="space-y-3">
              {siteData.visit.hours.map((h, i) => (
                <div key={i} className="flex justify-between items-baseline">
                  <span className="text-cream/60 font-light text-sm">{h.days}</span>
                  <span className={`text-sm font-light ${
                    h.time === "Closed" ? "text-cream/30" : "text-gold/70"
                  }`}>{h.time}</span>
                </div>
              ))}
            </div>

            {/* Happy Hour callout */}
            {siteData.visit.happyHour && (
              <div className="mt-6 pt-5 border-t border-gold/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: '#c9a96e' }}>Happy Hour</span>
                </div>
                <p className="text-cream/60 text-sm font-light">{siteData.visit.happyHour.days}</p>
                <p className="text-lg font-heading" style={{ color: '#c9a96e' }}>{siteData.visit.happyHour.deal}</p>
              </div>
            )}
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-[#161412] border border-gold/10 rounded-sm p-8 hover:border-gold/25 transition-colors duration-500"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <Phone className="text-gold" size={18} />
              </div>
              <h3 className="font-heading text-xl text-cream">Contact</h3>
            </div>
            <div className="space-y-4">
              <a
                href={`tel:${siteData.visit.phone}`}
                className="flex items-center gap-3 text-cream/60 hover:text-gold transition-colors font-light"
              >
                <Phone size={14} className="text-gold/50" />
                {siteData.visit.phone}
              </a>
              <a
                href={`mailto:${siteData.visit.email}`}
                className="flex items-center gap-3 text-cream/60 hover:text-gold transition-colors font-light"
              >
                <Mail size={14} className="text-gold/50" />
                {siteData.visit.email}
              </a>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-gold/10">
              <a href={siteData.visit.social.instagram} target="_blank" rel="noopener noreferrer" className="text-cream/40 hover:text-gold transition-colors duration-300">
                <InstagramIcon />
              </a>
              <a href={siteData.visit.social.facebook} target="_blank" rel="noopener noreferrer" className="text-cream/40 hover:text-gold transition-colors duration-300">
                <FacebookIcon />
              </a>
              <a href={siteData.visit.social.tiktok} target="_blank" rel="noopener noreferrer" className="text-cream/40 hover:text-gold transition-colors duration-300">
                <TikTokIcon />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Private Events */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 bg-[#161412] border border-gold/10 rounded-sm p-8 md:p-12 text-center hover:border-gold/25 transition-colors duration-500"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <PartyPopper className="text-gold" size={22} />
            <h3 className="font-heading text-xl md:text-2xl text-cream">Private Events & Reservations</h3>
          </div>
          <p className="text-cream/50 font-light max-w-2xl mx-auto leading-relaxed">
            {siteData.visit.reservations}
          </p>
        </motion.div>

        {/* Map Embed */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 rounded-sm overflow-hidden border border-gold/10"
        >
          <iframe
            title="Split Decisions Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.5!2d-73.079!3d41.344!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e87e1234567890%3A0x1234567890!2s8+Water+St%2C+Ansonia%2C+CT+06401!5e0!3m2!1sen!2sus!4v1234567890"
            className="w-full h-[300px] md:h-[400px] grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-700"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
};
